'use client';

import React from 'react';

interface ChatLoadingStateProps {
  onBackToList: () => void;
  meridianLogoUrl?: string;
}

/**
 * Loading state component with animated skeleton
 * Shows while chat is being loaded from the server
 */
export function ChatLoadingState({ onBackToList, meridianLogoUrl = '/meridian.png' }: ChatLoadingStateProps) {
  return (
    <>
      {/* Loading State Header */}
      <div className="border-b border-border px-6 py-4 flex justify-between items-center bg-card shadow-sm">
        <div className="flex items-center gap-3">
          <img
            src={meridianLogoUrl}
            alt="Meridian"
            className="w-8 h-8 rounded-full object-cover"
          />
          <h1 className="text-xl font-semibold text-card-foreground">
            Loading chat
          </h1>
        </div>
      </div>

      {/* Loading Content */}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          {/* Meridian Icon with Animation */}
          <div className="mb-6 relative">
            <div
              className="absolute inset-0 bg-gradient-to-br from-brand-accent/20 to-brand-light/20 rounded-full blur-xl animate-pulse"
              style={{ width: '120px', height: '120px', left: '50%', transform: 'translateX(-50%)' }}
            ></div>
            <img
              src={meridianLogoUrl}
              alt="Meridian"
              className="w-24 h-24 rounded-full object-cover relative shadow-lg animate-bounce mx-auto"
            />
          </div>

          {/* Loading Text */}
          <p className="text-lg font-semibold text-card-foreground mb-2">
            Loading your chat
          </p>
          <p className="text-text-secondary text-sm mb-8">
            Fetching conversation history
          </p>

          {/* Animated Loading Bars */}
          <div className="w-64 space-y-3 mx-auto">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-3 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-primary rounded-full"
                    style={{
                      animation: `shimmer 2s infinite`,
                      animationDelay: `${i * 0.2}s`,
                      width: '100%',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Dots Animation */}
          <div className="mt-8 flex items-center justify-center gap-1">
            <span
              className="inline-block w-2 h-2 bg-primary rounded-full animate-bounce"
              style={{ animationDelay: '0s' }}
            ></span>
            <span
              className="inline-block w-2 h-2 bg-primary rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            ></span>
            <span
              className="inline-block w-2 h-2 bg-primary rounded-full animate-bounce"
              style={{ animationDelay: '0.4s' }}
            ></span>
          </div>
        </div>
      </div>
    </>
  );
}
